import i18n from "../i18n";
import React, { useState } from "react";
import {
  IconTrash,
  IconPlus,
  IconGift,
  IconCoin,
  IconCalculator,
  IconCircleMinus,
  IconCirclePlus,
} from "@tabler/icons";
import {
  TextInput,
  Select,
  Button,
  Paper,
  Group,
  Stack,
  Divider,
  Box,
  Accordion,
  Text,
  ActionIcon,
  NumberInput,
  Textarea,
  Card,
  Tabs,
  FileInput,
  Image,
} from "@mantine/core";
import _ from "lodash";
import { useFormatter } from "../hooks/useFomatter";

// Comparison operators for conditions
const OPERATORS = [
  { value: ">", label: "Greater than (>)" },
  { value: ">=", label: "Greater than or equal (≥)" },
  { value: "<", label: "Less than (<)" },
  { value: "<=", label: "Less than or equal (≤)" },
  { value: "=", label: "Equal to (=)" },
  { value: "!=", label: "Not equal to (≠)" },
];

// Currency options
const CURRENCIES = [
  { value: "HKD", label: "HKD" },
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
  { value: "EUR", label: "EUR" },
];

// Non-monetary perk options
const PERKS = [
  { value: "thailand_trip", label: "Thailand Trip" },
  { value: "japan_trip", label: "Japan Trip" },
  { value: "luxury_watch", label: "Luxury Watch" },
  { value: "iphone_latest", label: "Latest iPhone" },
  { value: "macbook_pro", label: "MacBook Pro" },
  { value: "tesla_model3", label: "Tesla Model 3" },
  { value: "vip_dining", label: "VIP Dining Experience" },
  { value: "spa_weekend", label: "Spa Weekend" },
  { value: "golf_membership", label: "Golf Club Membership" },
  { value: "wine_collection", label: "Premium Wine Collection" },
];

const ConditionForm = ({ condition, onUpdate, index, metrics }) => {
  const [formatter] = useFormatter();

  const updateCondition = (field, value) => {
    const updatedCondition = { ...condition, [field]: value };
    onUpdate(index, updatedCondition);
  };

  const metricOptions =
    metrics?.map((m) => ({
      value: m.name,
      label: m.name,
    })) || [];

  return (
    <Group grow align="end">
      <Select
        label="Metric"
        value={condition.metric || ""}
        onChange={(value) => updateCondition("metric", value)}
        data={metricOptions}
        placeholder="Select metric"
        required
        size="xs"
      />

      <Select
        label="Operator"
        value={condition.operator || ""}
        onChange={(value) => updateCondition("operator", value)}
        data={OPERATORS}
        placeholder="Select operator"
        required
        size="xs"
      />

      <NumberInput
        label="Value"
        value={condition.value || ""}
        onChange={(value) => updateCondition("value", value)}
        placeholder="Enter value"
        required
        size="xs"
        parser={formatter.numberParser}
        formatter={formatter.numberFormat}
      />
    </Group>
  );
};

const RewardForm = ({ reward, onUpdate, index, metrics }) => {
  const [formatter] = useFormatter();

  const updateReward = (field, value) => {
    // console.log("RewardForm updateReward:", {
    //   field,
    //   value,
    //   index,
    //   currentReward: reward,
    // });
    const updatedReward = { ...reward, [field]: value };
    // console.log("Updated reward:", updatedReward);
    onUpdate(index, updatedReward);
  };

  const metricOptions =
    metrics?.map((m) => ({
      value: m.name,
      label: m.name,
    })) || [];

  // Ensure we have a valid reward type, fallback to monetary if empty
  const currentReward = reward.type ? reward : { ...reward, type: "monetary" };

  return (
    <Group grow align="end">
      <Select
        label="Reward Type"
        value={currentReward.type}
        onChange={(value) => {
          // Single update call to avoid multiple re-renders
          const updatedReward = { ...reward, type: value };

          if (value === "monetary") {
            updatedReward.perk = "";
            updatedReward.amountType = updatedReward.amountType || "fixed";
            updatedReward.currency = updatedReward.currency || "HKD";
          } else if (value === "non-monetary") {
            updatedReward.amount = "";
            updatedReward.amountFormula = "";
            updatedReward.amountType = "";
            updatedReward.currency = "";
          }

          onUpdate(index, updatedReward);
        }}
        data={[
          { value: "monetary", label: "Monetary" },
          { value: "non-monetary", label: "Non-Monetary" },
        ]}
        placeholder="Select reward type"
        required
        clearable={false}
        searchable={false}
        size="xs"
      />

      {currentReward.type === "monetary" && (
        <>
          <Select
            label="Amount Type"
            value={currentReward.amountType || "fixed"}
            onChange={(value) => {
              // Single update call with all changes
              const updatedReward = { ...currentReward, amountType: value };
              if (value === "fixed") {
                updatedReward.amountFormula = "";
              } else if (value === "formula") {
                updatedReward.amount = "";
              }

              onUpdate(index, updatedReward);
            }}
            data={[
              { value: "fixed", label: "Fixed Amount" },
              { value: "formula", label: "Formula Based" },
            ]}
            defaultValue="fixed"
            clearable={false}
            searchable={false}
            size="xs"
          />

          <Select
            label="Currency"
            value={currentReward.currency || "HKD"}
            onChange={(value) => updateReward("currency", value)}
            data={CURRENCIES}
            defaultValue="HKD"
            required
            size="xs"
          />

          {(currentReward.amountType === "fixed" ||
            !currentReward.amountType) && (
            <NumberInput
              label="Amount"
              value={currentReward.amount || ""}
              onChange={(value) => updateReward("amount", value)}
              placeholder="e.g., 5000"
              required
              min={0}
              size="xs"
              parser={formatter.currencyParser}
              formatter={formatter.numberFormat}
            />
          )}

          {currentReward.amountType === "formula" && (
            <Textarea
              label="Formula"
              value={currentReward.amountFormula || ""}
              onChange={(e) => updateReward("amountFormula", e.target.value)}
              placeholder="e.g., totalPremium * 0.01"
              required
              minRows={1}
              autosize
              size="xs"
            />
          )}
        </>
      )}

      {currentReward.type === "non-monetary" && (
        <Select
          label="Perk"
          value={currentReward.perk || ""}
          onChange={(value) => updateReward("perk", value)}
          data={PERKS}
          placeholder="Select perk"
          required
          searchable
          size="xs"
        />
      )}
    </Group>
  );
};

const TierForm = ({ tier, onUpdate, index, metrics }) => {
  const updateTier = (field, value) => {
    const updatedTier = { ...tier, [field]: value };
    onUpdate(index, updatedTier);
  };

  const addCondition = () => {
    const conditions = tier.conditions || [];
    conditions.push({ metric: "", operator: "", value: "" });
    updateTier("conditions", [...conditions]);
  };

  const updateCondition = (condIndex, updatedCondition) => {
    const conditions = [...(tier.conditions || [])];
    conditions[condIndex] = updatedCondition;
    updateTier("conditions", conditions);
  };

  const removeCondition = (condIndex) => {
    const conditions = [...(tier.conditions || [])];
    conditions.splice(condIndex, 1);
    updateTier("conditions", conditions);
  };

  const addReward = () => {
    const rewards = tier.rewards || [];
    rewards.push({
      type: "monetary", // Default to monetary
      amountType: "fixed",
      amount: "",
      amountFormula: "",
      currency: "HKD",
      perk: "",
    });
    updateTier("rewards", [...rewards]);
  };

  const updateReward = (rewardIndex, updatedReward) => {
    const rewards = [...(tier.rewards || [])];
    rewards[rewardIndex] = updatedReward;
    updateTier("rewards", rewards);
  };

  const removeReward = (rewardIndex) => {
    const rewards = [...(tier.rewards || [])];
    rewards.splice(rewardIndex, 1);
    updateTier("rewards", rewards);
  };

  return (
    <Tabs defaultValue="basic" variant="outline" mt="xl">
      <Tabs.List>
        <Tabs.Tab value="basic" leftSection={<IconGift size={14} />}>{i18n.t("Basic Info")}</Tabs.Tab>
        <Tabs.Tab value="conditions" leftSection={<IconCalculator size={14} />}>
          Conditions ({(tier.conditions || []).length})
        </Tabs.Tab>
        <Tabs.Tab value="rewards" leftSection={<IconCoin size={14} />}>
          Rewards ({(tier.rewards || []).length})
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="basic" pt="md">
        <Stack spacing="sm">
          <Group grow>
            <TextInput
              label="Tier Name"
              value={tier.name || ""}
              onChange={(e) => updateTier("name", e.target.value)}
              placeholder="e.g., Bronze Tier, Gold Achievement"
              required
              size="xs"
              autoComplete="off"
            />
            <TextInput
              label="Chinese Name"
              value={tier.cname || ""}
              onChange={(e) => updateTier("cname", e.target.value)}
              placeholder="e.g., 青铜级别, 黄金成就"
              size="xs"
              autoComplete="off"
            />
          </Group>

          <Textarea
            label="Description"
            value={tier.description || ""}
            onChange={(e) => updateTier("description", e.target.value)}
            placeholder="Describe the requirements and benefits of this tier..."
            minRows={3}
            size="xs"
          />

          <Box>
            <Text size="xs" mb={4} weight={500}>{i18n.t("Background Image")}</Text>
            <FileInput
              placeholder="Drop image here or click to select"
              accept="image/*"
              value={tier.backgroundImage || null}
              onChange={(file) => updateTier("backgroundImage", file)}
              size="xs"
            />
            {tier.backgroundImage && (
              <Box mt="xs">
                <Image
                  src={
                    typeof tier.backgroundImage === "string"
                      ? tier.backgroundImage
                      : URL.createObjectURL(tier.backgroundImage)
                  }
                  alt="Background preview"
                  height={100}
                  fit="cover"
                  radius="sm"
                />
              </Box>
            )}
          </Box>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="conditions" pt="md">
        <Stack spacing="md">
          <Group>
            <Text weight={500} size="sm" color="dimmed">
              All conditions must be met to qualify for this tier
            </Text>
          </Group>

          {metrics.length === 0 && (
            <Card withBorder p="md">
              <Text size="sm" color="orange">
                📋 Please define metrics first before creating reward conditions
              </Text>
            </Card>
          )}

          <Stack spacing="xs">
            {(tier.conditions || []).map((condition, condIndex) => (
              <Group key={condIndex} align="end">
                <Box style={{ flex: 1 }}>
                  <ConditionForm
                    condition={condition}
                    onUpdate={updateCondition}
                    index={condIndex}
                    metrics={metrics}
                  />
                </Box>
                <ActionIcon
                  onClick={() => removeCondition(condIndex)}
                  size="sm"
                >
                  <IconCircleMinus size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>

          <Group spacing="xs">
            <ActionIcon
              onClick={addCondition}
              disabled={metrics.length === 0}
              title="Add Condition"
            >
              <IconCirclePlus size={16} />
            </ActionIcon>
            <Text size="sm" color="dimmed">
              Add
            </Text>
          </Group>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="rewards" pt="md">
        <Stack spacing="md">
          <Group>
            <Text weight={500} size="sm" color="dimmed">
              Define what rewards agents receive when qualifying for this tier
            </Text>
          </Group>

          <Stack spacing="xs">
            {(tier.rewards || []).map((reward, rewardIndex) => (
              <Group key={rewardIndex} align="end" spacing="md">
                <Box style={{ flex: 1 }}>
                  <RewardForm
                    reward={reward}
                    onUpdate={updateReward}
                    index={rewardIndex}
                    metrics={metrics}
                  />
                </Box>
                <ActionIcon onClick={() => removeReward(rewardIndex)} size="sm">
                  <IconCircleMinus size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>

          <Group spacing="xs">
            <ActionIcon onClick={addReward} title="Add Reward">
              <IconCirclePlus size={16} />
            </ActionIcon>
            <Text size="sm" color="dimmed">
              Add
            </Text>
          </Group>
        </Stack>
      </Tabs.Panel>
    </Tabs>
  );
};

const CompensationReward = ({ form, name, ...props }) => {
  const tiers = _.get(form.values, name) ?? [];
  const metrics = _.get(form.values, "metrics") ?? [];

  const onAddTier = () => {
    const newTier = {
      name: "",
      conditions: [],
      rewards: [],
    };
    const updatedTiers = [...tiers, newTier];
    form.setFieldValue(name, updatedTiers);
  };

  const onRemoveTier = (index) => {
    const updatedTiers = [...tiers];
    updatedTiers.splice(index, 1);
    form.setFieldValue(name, updatedTiers);
  };

  const updateTier = (index, updatedTier) => {
    const updatedTiers = [...tiers];
    updatedTiers[index] = updatedTier;
    form.setFieldValue(name, updatedTiers);
  };

  const getTierSummary = (tier) => {
    const conditionsCount = tier.conditions?.length || 0;
    const rewardsCount = tier.rewards?.length || 0;
    const monetaryRewards =
      tier.rewards?.filter((r) => r.type === "monetary")?.length || 0;
    const nonMonetaryRewards =
      tier.rewards?.filter((r) => r.type === "non-monetary")?.length || 0;

    return `${conditionsCount} conditions, ${rewardsCount} rewards (${monetaryRewards} monetary, ${nonMonetaryRewards} perks)`;
  };

  return (
    <>
      {metrics.length === 0 && (
        <Card withBorder mb="md" p="md">
          <Text size="sm" color="orange">
            📋 Please define metrics first before creating reward tiers
          </Text>
        </Card>
      )}

      <Stack spacing="md" mb="xl">
        <Accordion multiple>
          {tiers.map((tier, index) => (
            <Accordion.Item key={index} value={`tier-${index}`} mb="md">
              <Accordion.Control>
                <Group>
                  <IconGift size={16} />
                  <Text weight={500}>{tier.name || `Tier ${index + 1}`}</Text>
                  <Text size="sm" color="dimmed">
                    {getTierSummary(tier)}
                  </Text>
                  <div style={{ marginLeft: "auto" }}>
                    <ActionIcon
                      variant="filled"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveTier(index);
                      }}
                    >
                      <IconTrash size={13} />
                    </ActionIcon>
                  </div>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {/* <Paper withBorder p="sm"> */}
                <TierForm
                  tier={tier}
                  onUpdate={updateTier}
                  index={index}
                  metrics={metrics}
                />
                {/* </Paper> */}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>

      <Button
        onClick={onAddTier}
        disabled={metrics.length === 0}
        title="Add Reward Tier"
        size="xs"
        leftIcon={<IconCirclePlus size={16} />}
      >{i18n.t("Add Reward Tier")}</Button>

      {/* {tiers.length > 0 && (
        <Box mt="xl" p="md">
          <Text size="sm" weight={500} mb="xs">
            🏆 Reward Tiers Summary ({tiers.length} total)
          </Text>
          {tiers.map((tier, index) => (
            <Text key={index} size="xs" color="dimmed">
              • {tier.name || `Tier ${index + 1}`}: {getTierSummary(tier)}
            </Text>
          ))}
        </Box>
      )} */}
    </>
  );
};

export default CompensationReward;
